import React, { useState, useRef, useEffect } from 'react';
import {
    School,
    CheckCircle,
    ChevronDown,
    ChevronUp,
    AlertTriangle,
    GraduationCap,
    Upload,
    BookOpen,
    FileText,
    Copy,
    Printer,
    File as FileIcon,
    Image as ImageIcon,
    X,
    Loader
} from 'lucide-react';
import { fetchWithGeminiResilience } from './geminiClient';
import { marked } from 'marked';

// Configurar marked
marked.setOptions({
    breaks: true,
    gfm: true
});

function parseMarkdownToHtml(markdown) {
    if (!markdown) return '';

    // 1. Separar a Parte 1 (que contém os cards das questões) das Partes 2 e 3 (Global e Plano de Ação)
    const sections = markdown.split(/##\s+Parte\s+2/i);
    const parte1 = sections[0];
    const parte2e3 = sections[1] ? '## Parte 2' + sections[1] : '';

    let htmlResult = '';

    // 2. Processar a Parte 1 (gerando cards de questões baseados em delimitadores "###")
    const parts = parte1.split(/^###\s+/gm);
    if (parts.length > 1) {
        htmlResult += marked.parse(parts[0]); // Renderiza o cabeçalho e textos de introdução da Parte 1
        
        for (let i = 1; i < parts.length; i++) {
            const partContent = parts[i];
            const lines = partContent.split('\n');
            const titleLine = lines[0].trim();
            const bodyContent = lines.slice(1).join('\n');

            // Regex multilinha para capturar cada campo (ex: * **Dificuldade:** Fácil)
            const fieldRegex = /^[*-]\s+\*\*(.*?):\*\*\s*([\s\S]*?)(?=(?:^[*-]\s+\*\*|$))/gm;
            let formattedBody = '';
            let match;
            
            while ((match = fieldRegex.exec(bodyContent)) !== null) {
                const label = match[1].trim();
                const value = match[2].trim();
                
                // Converter o valor usando marked para formatar parágrafos, listas internas etc.
                const parsedValue = marked.parse(value);
                
                const isFullWidth = label.includes("Gabarito") || label.includes("Suporte") || label.includes("Distratores") || label.includes("Pedagogia") || label.includes("Análise") || label.includes("BNCC") || label.includes("ENEM") || label.includes("Alinhamento") || value.length > 80;
                const colClass = isFullWidth ? 'col-span-full bg-white p-3 rounded-lg border border-slate-100 shadow-sm mt-1' : 'bg-white p-2.5 rounded-lg border border-slate-100 shadow-sm';
                
                let processedValue = parsedValue;
                if (label.includes("Integridade") && value.toUpperCase().includes("ERRO GRAVE")) {
                    processedValue = `<span class="text-red-600 font-bold font-mono">${parsedValue}</span>`;
                }

                formattedBody += `
                    <div class="${colClass}">
                        <span class="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">${label}</span>
                        <div class="text-[12px] text-slate-700 leading-relaxed">${processedValue}</div>
                    </div>
                `;
            }

            // Fallback se não seguir a estrutura padrão
            if (!formattedBody) {
                formattedBody = `<div class="col-span-full bg-white p-3 rounded-lg border border-slate-100">${marked.parse(bodyContent)}</div>`;
            }

            htmlResult += `
                <div class="p-4 mb-5 border border-slate-200 border-l-4 border-l-orange-500 bg-slate-50 rounded-r-xl shadow-sm">
                    <h3 class="font-bold text-slate-800 text-sm mb-3 uppercase tracking-wide flex items-center gap-2">
                        <span class="w-2 h-2 rounded-full bg-orange-500 animate-pulse shrink-0"></span>
                        ${marked.parseInline(titleLine)}
                    </h3>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                        ${formattedBody}
                    </div>
                </div>
            `;
        }
    } else {
        htmlResult += marked.parse(parte1);
    }

    // 3. Processar a Parte 2 e a Parte 3
    if (parte2e3) {
        const partesFinais = parte2e3.split(/##\s+Parte\s+3/i);
        const parte2Html = marked.parse(partesFinais[0]);
        
        htmlResult += parte2Html;

        if (partesFinais[1]) {
            const parte3Html = marked.parse('## Parte 3' + partesFinais[1]);
            // Envelopar a Parte 3 (Plano de ação) em uma caixa estilizada azul
            htmlResult += `
                <div class="mt-8 p-6 bg-blue-50 border border-blue-200 rounded-2xl shadow-sm text-slate-800">
                    ${parte3Html}
                </div>
            `;
        }
    }

    // Aplicar estilizações de Badges globais
    htmlResult = htmlResult.replace(/(ERRO GRAVE|REPROVADA \/ REFORMULAR|REPROVADA)/g, '<span class="inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase rounded bg-red-100 text-red-700 border border-red-200 shadow-sm">$1</span>');
    htmlResult = htmlResult.replace(/(APROVADA PARA ENCAMINHAMENTO|APROVADA)/g, '<span class="inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase rounded bg-green-100 text-green-700 border border-green-200 shadow-sm">$1</span>');
    htmlResult = htmlResult.replace(/(APROVADA COM RESSALVAS)/g, '<span class="inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase rounded bg-yellow-100 text-yellow-700 border border-yellow-200 shadow-sm">$1</span>');

    return htmlResult;
}

export default function QuestionValidator({ apiKey }) {
    // Estado do formulário
    const [area, setArea] = useState("");
    const [disciplina, setDisciplina] = useState("");
    const [serie, setSerie] = useState("");
    const [trimestre, setTrimestre] = useState("");
    const [questoes, setQuestoes] = useState("");

    // Estado de arquivos
    const [attachments, setAttachments] = useState([]);

    // Estado da API/Validação
    const [validationReport, setValidationReport] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isDetecting, setIsDetecting] = useState(false); // Novo estado para detecção automática
    
    // Novos estados de validação por lote
    const [questoesList, setQuestoesList] = useState([]);
    const [statusValida, setStatusValida] = useState({}); // { [numero]: 'pendente' | 'validando' | 'concluido' | 'erro' }
    const [analisesIndividual, setAnalisesIndividual] = useState({}); // { [numero]: 'html...' }
    const [analiseGlobal, setAnaliseGlobal] = useState(null); // 'html...'
    const [validationStep, setValidationStep] = useState(""); // "extraindo" | "validando" | "concluindo" | ""

    // Estado de UI
    const [showApiKeyConfig, setShowApiKeyConfig] = useState(false);
    const [localApiKey, setLocalApiKey] = useState("");

    const fileInputRef = useRef(null);

    // Efeito para consolidar os relatórios individuais e globais no validationReport
    useEffect(() => {
        if (Object.keys(analisesIndividual).length > 0) {
            let fullReport = '<div class="space-y-6">';
            
            // Título
            fullReport += '<h1 class="text-2xl font-bold text-slate-900 border-b-2 border-slate-200 pb-2 mb-6">Relatório de Validação de Prova</h1>';
            
            // Adicionar Parte 1 (Cards das questões)
            fullReport += '<h2 class="text-xl font-bold text-orange-800 border-b border-orange-200 pb-2 mb-4">Parte 1: Análise Questão a Questão</h2>';
            
            // Ordenar as questões numericamente
            const sortedNums = Object.keys(analisesIndividual).map(Number).sort((a, b) => a - b);
            sortedNums.forEach(num => {
                fullReport += analisesIndividual[num];
            });
            
            // Adicionar Parte 2 e Parte 3
            if (analiseGlobal) {
                fullReport += analiseGlobal;
            }
            
            fullReport += '</div>';
            setValidationReport(fullReport);
        }
    }, [analisesIndividual, analiseGlobal]);

    // Listas de opções
    const disciplinasList = [
        "Língua Portuguesa", "Matemática", "História (History)", "Geografia (Geography)",
        "Language and Arts", "Ciências da Natureza (Science)", "Biologia",
        "Química", "Física", "Filosofia", "Sociologia",
        "Literatura", "Arte", "Physical Education",
        "Public Speaking", "Drama", "Creative Tech", "Music"
    ];

    // Chave de API efetiva (Prop ou fallback ou local)
    const effectiveApiKey = apiKey || localApiKey;

    const handleFileSelect = async (e) => {
        const files = Array.from(e.target.files);
        setError(null);

        const newAttachments = [...attachments];

        for (const file of files) {
            if (file.size > 4 * 1024 * 1024) {
                setError(`O arquivo ${file.name} é muito grande (máx 4MB).`);
                continue;
            }

            try {
                const base64 = await convertFileToBase64(file);
                newAttachments.push({
                    name: file.name,
                    mimeType: file.type,
                    data: base64.split(',')[1]
                });
            } catch (err) {
                setError(`Erro ao processar arquivo ${file.name}`);
            }
        }
        setAttachments(newAttachments);

        e.target.value = ''; // Reset input

        // Se adicionou pelo menos um arquivo novo, tenta detectar metadados do primeiro
        if (newAttachments.length > attachments.length) {
            // Pega o último adicionado (que é o primeiro do batch atual se attachments estava vazio, ou o último do array)
            // Na verdade, newAttachments tem TUDO. Vamos pegar o primeiro item DO LOTE ATUAL processado (que está no final do array se append)
            // Simplesmente pegamos o último item adicionado para analisar.
            const lastAtt = newAttachments[newAttachments.length - 1];
            if (lastAtt) {
                detectMetadata(lastAtt);
            }
        }
    };

    const detectMetadata = async (fileObj) => {
        if (!effectiveApiKey) return; // Silencioso se sem key

        setIsDetecting(true);
        try {
            const prompt = `
                Analise o documento anexo e identifique:
                1. Área do Conhecimento
                2. Disciplina
                3. Série/Ano Escolar
                4. Trimestre/Etapa

                Retorne APENAS um JSON estrito com este formato:
                {
                    "area": "X",
                    "disciplina": "Y",
                    "serie": "Z",
                    "trimestre": "W"
                }

                REGRAS DE VALORES (Use exatamente estes textos):
                - Area: "Ciências da Natureza", "Ciências Humanas", "Linguagens", "Matemática"
                - Serie: "4º Ano EF", "5º Ano EF", "6º Ano EF", "7º Ano EF", "8º Ano EF", "9º Ano EF", "1ª Série EM", "2ª Série EM", "3ª Série EM"
                - Trimestre: "1º Trimestre", "2º Trimestre", "3º Trimestre"

                Se não encontrar algum campo, retorne null ou string vazia.
            `;

            const payload = {
                contents: [{
                    parts: [
                        { text: prompt },
                        { inlineData: { mimeType: fileObj.mimeType, data: fileObj.data } }
                    ]
                }],
                generationConfig: { responseMimeType: "application/json" }
            };

            const response = await fetchWithGeminiResilience(effectiveApiKey, payload);

            const data = await response.json();

            if (data.error) {
                console.error("API Error in detectMetadata:", data.error);
                throw new Error(data.error.message || "Erro desconhecido na API do Gemini.");
            }

            const text = data.candidates?.[0]?.content?.parts?.[0]?.text;
            if (text) {
                const json = JSON.parse(text);
                if (json.area) setArea(json.area);
                if (json.disciplina) {
                    if (disciplinasList.includes(json.disciplina)) setDisciplina(json.disciplina);
                    else if (json.disciplina.includes("Portuguesa")) setDisciplina("Língua Portuguesa");
                    else if (json.disciplina.includes("Art")) setDisciplina("Language and Arts");
                }
                if (json.serie) setSerie(json.serie);
                if (json.trimestre) setTrimestre(json.trimestre);
            } else {
                console.warn("Unexpected API response structure in detectMetadata:", data);
            }
        } catch (e) {
            console.error("Autofill error:", e);
            // Non-blocking error for autofill, just log it.
        } finally {
            setIsDetecting(false);
        }
    };

    const convertFileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = error => reject(error);
        });
    };

    const removeAttachment = (index) => {
        setAttachments(attachments.filter((_, i) => i !== index));
    };

    const copyToClipboard = () => {
        if (!validationReport) return;
        // Strip HTML tags for clipboard text or use a ref to the content
        const content = document.getElementById('report-content')?.innerText;
        if (content) {
            navigator.clipboard.writeText(content);
            alert('Relatório copiado para a área de transferência!');
        }
    };

    const handlePrint = () => {
        window.print();
    };

    const generateValidation = async () => {
        setError(null);
        setValidationReport(null);
        setQuestoesList([]);
        setStatusValida({});
        setAnalisesIndividual({});
        setAnaliseGlobal(null);

        if (!effectiveApiKey || effectiveApiKey.length < 10) {
            setError('Por favor, insira uma chave de API do Google Gemini válida.');
            return;
        }

        if (!questoes.trim() && attachments.length === 0) {
            setError('Por favor, insira as questões (texto) ou faça upload de um arquivo.');
            return;
        }

        if (!area || !disciplina || !serie || !trimestre) {
            setError('Por favor, preencha todos os campos do cabeçalho da avaliação.');
            return;
        }

        setIsLoading(true);
        setValidationStep("extraindo");

        // Obter os anexos ou textos
        const contentParts = [];
        if (questoes.trim()) {
            contentParts.push({ text: `Texto das questões:\n\n${questoes}` });
        } else {
            contentParts.push({ text: "Questões no documento anexo." });
        }

        attachments.forEach(att => {
            contentParts.push({
                inlineData: {
                    mimeType: att.mimeType,
                    data: att.data
                }
            });
        });

        try {
            // Fase 1: Extrair a lista de questões em JSON
            const extractionPrompt = `
                Você deve ler o documento fornecido ou texto e extrair todas as questões de avaliação de forma individual.
                
                MUITO IMPORTANTE - LEITURA COMPLETA DE TABELAS E CÉLULAS:
                O documento pode conter tabelas, caixas de texto, quadros ou células de tabelas. Você deve ler e extrair TODO o conteúdo textual contido dentro de cada célula, linha ou cabeçalho de tabela. Textos de suporte, títulos de textos (como 'Texto 1', 'Texto 2', 'Texto 3') ou qualquer enunciado inserido dentro de células de tabelas DEVEM ser lidos integralmente e associados à respectiva questão como parte do texto de apoio ou comando. Não ignore nenhuma célula.
                
                Retorne EXCLUSIVAMENTE um array JSON em que cada objeto representa uma única questão com as propriedades:
                - "numero": (número inteiro sequencial iniciando em 1)
                - "texto": (o enunciado completo da questão, incluindo textos de apoio que a precedem nas tabelas/células e suas alternativas se for múltipla escolha)
                
                Exemplo de Saída:
                [
                  {
                    "numero": 1,
                    "texto": "Texto completo da questão 1..."
                  }
                ]
                
                ATENÇÃO: Retorne APENAS o código JSON estrito. Não adicione cabeçalhos, markdown de bloco de código (como \`\`\`json) ou explicações adicionais fora do array JSON.
            `;

            const payloadExtraction = {
                contents: [{ parts: [...contentParts, { text: extractionPrompt }] }],
                generationConfig: { 
                    responseMimeType: "application/json" 
                }
            };

            const responseEx = await fetchWithGeminiResilience(effectiveApiKey, payloadExtraction);
            if (!responseEx.ok) {
                throw new Error(`Falha ao conectar com o Gemini (Extração: ${responseEx.status})`);
            }

            const dataEx = await responseEx.json();
            let jsonText = dataEx.candidates?.[0]?.content?.parts?.[0]?.text;
            if (!jsonText) {
                throw new Error("Não foi possível extrair a lista de questões.");
            }

            // Sanitizar possível markdown de código retornado pela IA mesmo com a instrução de json estrito
            jsonText = jsonText.trim();
            if (jsonText.startsWith('```')) {
                const lines = jsonText.split('\n');
                if (lines[0].startsWith('```')) lines.shift();
                if (lines[lines.length - 1].startsWith('```')) lines.pop();
                jsonText = lines.join('\n').trim();
            }

            const qList = JSON.parse(jsonText);
            if (!Array.isArray(qList) || qList.length === 0) {
                throw new Error("Nenhuma questão foi identificada na avaliação.");
            }

            setQuestoesList(qList);
            
            // Configurar progresso inicial das questões
            const initialStatus = {};
            qList.forEach(q => {
                initialStatus[q.numero] = 'pendente';
            });
            setStatusValida(initialStatus);
            setValidationStep("validando");

            // Fase 2: Validar questão por questão sequencialmente (experiência progressiva e controle de limite de taxa)
            const reports = {};
            for (let i = 0; i < qList.length; i++) {
                const q = qList[i];
                setStatusValida(prev => ({ ...prev, [q.numero]: 'validando' }));

                try {
                    const singlePrompt = `
                        Você é um Coordenador Pedagógico Especialista e Validador de Avaliações rigoroso.
                        Sua tarefa é validar especificamente a seguinte QUESTÃO escolar:
                        
                        QUESTÃO A VALIDAR:
                        ${q.texto}
                        
                        CONTEXTO DA AVALIAÇÃO:
                        - Área: ${area}
                        - Disciplina: ${disciplina}
                        - Série: ${serie}
                        - Trimestre: ${trimestre}
                        
                        Calibração do Nível de Ensino:
                        - 4º e 5º Anos (Anos Iniciais): Linguagem clara, direta e acessível. Raciocínio concreto. Foco em alfabetização consolidada.
                        - 6º ao 9º Ano: Pensamento abstrato, textos moderados.
                        - Ensino Médio: Matriz ENEM, complexidade e análise profunda.
                        
                        Você deve validar esta questão seguindo estritamente estes critérios e gerar o relatório EXCLUSIVAMENTE neste formato de lista Markdown:
                        
                        ### Questão ${q.numero}
                        * **Dificuldade:** [Fácil / Médio / Difícil]
                        * **Revisão Gramatical:** [Erros ou "Sem erros"]
                        * **Suporte e Coerência Textual (Obrigatório verificar citações e imagens):** [Examine minuciosamente os arquivos e imagens anexados (se houver). Se o enunciado ou alternativas citam frases do texto de apoio, transcreva a frase exata que comprova. Se a questão diz 'observe a imagem', 'analise o gráfico' ou 'veja o mapa', você DEVE buscar e verificar visualmente no documento anexo se a imagem, gráfico ou mapa correspondente está fisicamente lá. Se estiver presente no anexo, valide como 'Existente'. Apenas aponte inconsistência e use 'ERRO GRAVE' se o recurso realmente não constar no arquivo original anexado (inclusive tabelas e quadros).]
                        * **Gabarito Comentado (Explicação Detalhada):** [Indique a resposta/alternativa correta e explique de forma pedagógica, teórica e minuciosa o porquê dela ser a correta.]
                        * **Análise dos Distratores:** [Explique o erro conceitual ou de raciocínio de cada alternativa incorreta de maneira sucinta porém clara.]
                        * **Qualidade Pedagógica:** [Habilidade avaliada (Bloom) vs memorização pura]
                        * **Alinhamento BNCC (Habilidade Ideal/Próxima):** [Identifique obrigatoriamente o código e a descrição literal completa da habilidade da BNCC em que a questão se enquadra. Se a questão contiver desvios, inconsistências ou faltar indicação original, indique expressamente a habilidade mais adequada e próxima para o conteúdo abordado, com a justificativa (ex: 'Sugerida Habilidade Próxima: EF09Mat03...').]
                        * **Alinhamento ENEM (Habilidade Ideal/Próxima):** [Para o Ensino Médio, identifique a Competência de Área e o código/descrição literal completa da Habilidade da Matriz do ENEM correspondente. Em caso de desvio do objeto de conhecimento da questão, indique e recomende de forma clara a habilidade próxima mais aderente com a respectiva justificativa. Se for Ensino Fundamental, indique 'Não aplicável (Ensino Fundamental)']
                        * **Integridade:** [Status - se faltar dados ou se houver citação fantasma no texto, use as palavras 'ERRO GRAVE: Inconsistência textual']
                    `;

                    // Injetar os anexos base64 (PDFs/Imagens) na chamada de validação individual
                    const partsSingle = [{ text: singlePrompt }];
                    attachments.forEach(att => {
                        partsSingle.push({
                            inlineData: {
                                mimeType: att.mimeType,
                                data: att.data
                            }
                        });
                    });

                    const payloadSingle = {
                        contents: [{ parts: partsSingle }],
                        generationConfig: { temperature: 0.4 }
                    };

                    const responseSingle = await fetchWithGeminiResilience(effectiveApiKey, payloadSingle);
                    if (!responseSingle.ok) {
                        throw new Error(`Erro na API (${responseSingle.status})`);
                    }

                    const dataSingle = await responseSingle.json();
                    const textReport = dataSingle.candidates?.[0]?.content?.parts?.[0]?.text;

                    if (textReport) {
                        const htmlReport = parseMarkdownToHtml(textReport);
                        reports[q.numero] = htmlReport;
                        setAnalisesIndividual(prev => ({ ...prev, [q.numero]: htmlReport }));
                        setStatusValida(prev => ({ ...prev, [q.numero]: 'concluido' }));
                    } else {
                        throw new Error("Resposta de validação vazia da IA.");
                    }
                } catch (singleErr) {
                    console.error(`Erro ao validar questão ${q.numero}:`, singleErr);
                    setStatusValida(prev => ({ ...prev, [q.numero]: 'erro' }));
                    const errHtml = `
                        <div class="p-4 mb-5 border border-red-200 border-l-4 border-l-red-500 bg-red-50 rounded-r-xl shadow-sm text-red-800 text-xs">
                            <h3 class="font-bold text-red-950 uppercase mb-2">Questão ${q.numero} - Erro na Validação</h3>
                            <p>${singleErr.message || 'Falha ao processar resposta do Gemini.'}</p>
                        </div>
                    `;
                    reports[q.numero] = errHtml;
                    setAnalisesIndividual(prev => ({ ...prev, [q.numero]: errHtml }));
                }
            }

            // Fase 3: Validação Global
            setValidationStep("concluindo");

            // Criar um resumo das análises individuals para a IA consolidar a nota geral
            const summaryList = Object.entries(reports).map(([num, reportHtml]) => {
                const tempDiv = document.createElement("div");
                tempDiv.innerHTML = reportHtml;
                const text = tempDiv.innerText.replace(/\s+/g, ' ').substring(0, 300);
                return `Questão ${num}: ${text}...`;
            }).join('\n');

            // Texto integral das questões (necessário para a análise de tempo)
            const textoIntegralProva = qList.map(q => `--- QUESTÃO ${q.numero} ---\n${q.texto}`).join('\n\n');

            const globalPrompt = `
                Você é um Coordenador Pedagógico Especialista.
                Analise os seguintes resumos das validações individuais de cada questão da prova e gere a Análise Global final da prova:
                
                DADOS DO CABEÇALHO:
                - Área: ${area} | Disciplina: ${disciplina} | Série: ${serie} | Trimestre: ${trimestre}
                
                RESUMOS DA VALIDAÇÃO INDIVIDUAL DAS QUESTÕES:
                ${summaryList}
                
                TEXTO INTEGRAL DAS QUESTÕES (use para a análise de tempo; considere textos-base, imagens, tabelas e comandos):
                ${textoIntegralProva}
                
                REGRAS OBRIGATÓRIAS PARA A ANÁLISE DE TEMPO:
                - NÃO estime apenas pelo número nominal de questões. Trate a prova como um conjunto de tarefas cognitivas.
                - Considere o perfil etário e escolar típico da série (${serie}): velocidade de leitura silenciosa e velocidade de escrita manual compatíveis com a idade.
                - Uma questão com vários comandos (ex.: "identifique, classifique, justifique e explique") conta como VÁRIAS operações cognitivas, cada uma com seu tempo.
                - Textos-base: avalie extensão, complexidade vocabular, complexidade sintática, densidade conceitual, necessidade de inferência e necessidade de reconsulta durante as questões.
                - Discursivas: avalie quantidade de elementos exigidos na resposta, nível de elaboração, necessidade de justificar, articulação conteúdo/interpretação, extensão provável da resposta e tempo físico de escrita para a idade.
                - O ALUNO MÉDIO é o parâmetro principal para julgar a adequação da duração.
                - O aluno de ritmo lento NÃO considera adaptações ou necessidades educacionais específicas.
                
                Gere a saída EXCLUSIVAMENTE em formato Markdown seguindo esta estrutura:
                
                ## Parte 2: Análise Global da Prova
                * **Adequação à Série:** [Explicação geral de adequação]
                * **Somatório das Questões:** [Status e distribuição geral das notas]
                * **Equilíbrio de Dificuldade:** [A prova atingiu o Target 30/40/30? Analise a proporção real com base nas dificuldades individuais analisadas]
                * **Nota de Nível Geral:** [Nota de 0 a 10 com base nos critérios]
                * **Média Esperada da Turma:** [Média estimada]
                * **Resumo da Análise:** [Breve resumo global]
                
                ### Análise de Tempo de Realização
                
                | Tarefa cognitiva | Aluno alto desempenho | Aluno médio | Aluno ritmo lento |
                |---|---|---|---|
                | Leitura das orientações gerais | | | |
                | Leitura e compreensão de textos-base, tirinhas, gráficos, imagens e outros estímulos | | | |
                | Releitura dos textos durante a resolução | | | |
                | Interpretação dos comandos | | | |
                | Resolução de questões objetivas | | | |
                | Elaboração de respostas discursivas | | | |
                | Análise gramatical/matemática/científica/conceitual | | | |
                | Transcrição, cálculos ou desenvolvimento de procedimentos | | | |
                | Revisão final das respostas | | | |
                | **TOTAL ESTIMADO** | | | |
                
                | Questão | Nº de operações cognitivas (comandos) | Tipo | Tempo aluno médio | Observação (texto-base, elementos exigidos, extensão da escrita) |
                |---|---|---|---|---|
                [Uma linha por questão]
                
                * **Perfil 1 – Alto desempenho:** [Tempo provável de conclusão e justificativa]
                * **Perfil 2 – Aluno médio (parâmetro principal):** [Tempo provável e justificativa]
                * **Perfil 3 – Ritmo mais lento:** [Tempo provável e justificativa]
                * **Textos-base mais exigentes:** [Quais e por quê: extensão, vocabulário, sintaxe, densidade, inferência, reconsulta]
                * **Questões com maior carga de tempo:** [Quais, quantos comandos e por quê]
                * **Duração recomendada da prova:** [Ex.: 1 aula de 50 min / 2 aulas de 50 min, com base no aluno médio]
                * **Adequação do tempo:** [ADEQUADA / LONGA DEMAIS / CURTA DEMAIS, com sugestões de ajuste (cortar comandos, reduzir textos, transformar discursiva em objetiva etc.)]
                
                * **VEREDITO FINAL:** [APROVADA PARA ENCAMINHAMENTO / APROVADA COM RESSALVAS / REPROVADA / REFORMULAR]
                
                ## Parte 3: Plano de Ação para Nota 10
                > Se a Nota de Nível Geral for menor que 10,0, liste o que o professor precisa fazer para atingir a nota máxima. Obrigatório: Se o desequilíbrio for na dificuldade (ex: prova muito fácil), sugira como aumentar o nível das questões (ex: mudar o verbo de ação de 'Identifique' para 'Analise', ou contextualizar o enunciado com uma situação-problema complexa). Use a frase: "Para chegar até o 10, você deve..."
            `;

            const payloadGlobal = {
                contents: [{ parts: [{ text: globalPrompt }] }],
                generationConfig: { temperature: 0.3 }
            };

            const responseGlobal = await fetchWithGeminiResilience(effectiveApiKey, payloadGlobal);
            if (!responseGlobal.ok) {
                throw new Error(`Falha na análise global (${responseGlobal.status})`);
            }

            const dataGlobal = await responseGlobal.json();
            const textGlobal = dataGlobal.candidates?.[0]?.content?.parts?.[0]?.text;

            if (textGlobal) {
                const htmlGlobal = parseMarkdownToHtml(textGlobal);
                setAnaliseGlobal(htmlGlobal);
            } else {
                throw new Error("Análise global vazia retornado pelo Gemini.");
            }

        } catch (err) {
            console.error("Erro na validação geral:", err);
            setError(err.message || 'Erro inesperado na validação.');
        } finally {
            setValidationStep("");
            setIsLoading(false);
        }
    };

    return (
        <div className="h-full bg-slate-50 text-slate-800 font-sans flex flex-col overflow-y-auto w-full print:h-auto print:overflow-visible print:block print:bg-white">

            {/* Header */}
            <header className="bg-slate-950 text-white p-6 shadow-lg shrink-0 print:hidden">
                <div className="max-w-7xl mx-auto flex justify-between items-center">
                    <div className="flex items-center gap-4">
                        <div className="p-2 bg-slate-800 rounded-lg">
                            <School className="w-8 h-8 text-orange-500" />
                        </div>
                        <div>
                            <h1 className="text-xl font-bold">Validador Pedagógico</h1>
                            <p className="text-xs text-orange-200">Análise de Itens & Matrizes (BNCC/ENEM)</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        {effectiveApiKey ? (
                            <div className="bg-green-500/20 border border-green-500/50 px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-2">
                                <CheckCircle className="w-3 h-3" /> Sistema Pronto
                            </div>
                        ) : (
                            <button
                                onClick={() => setShowApiKeyConfig(!showApiKeyConfig)}
                                className="bg-red-500/20 border border-red-500/50 px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-2 hover:bg-red-500/30 transition-colors"
                            >
                                <AlertTriangle className="w-3 h-3" /> Configurar API
                            </button>
                        )}
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="max-w-7xl mx-auto p-4 md:p-8 flex flex-col lg:flex-row gap-6 w-full flex-1 print:block print:p-0 print:m-0 print:max-w-none">

                {/* Input Section - Hide when printing or if desired when result is shown (optional, keeping visible for edit) */}
                <div className={`flex-1 space-y-6 ${validationReport ? 'hidden lg:block lg:w-1/3' : 'w-full'} print:hidden`}>

                    {/* API Key Config (Collapsible) */}
                    {(!apiKey || showApiKeyConfig) && (
                        <div className="bg-white p-4 rounded-lg shadow-sm border border-slate-200">
                            <div className="flex justify-between items-center cursor-pointer mb-2" onClick={() => setShowApiKeyConfig(!showApiKeyConfig)}>
                                <h3 className="font-semibold text-slate-700 flex items-center gap-2">
                                    <AlertTriangle className="w-4 h-4 text-amber-500" /> Configuração
                                </h3>
                                {showApiKeyConfig ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                            </div>

                            {showApiKeyConfig && (
                                <div>
                                    <label className="block text-sm text-slate-600 mb-1">Google Gemini API Key</label>
                                    <input
                                        type="password"
                                        value={localApiKey}
                                        onChange={(e) => setLocalApiKey(e.target.value)}
                                        placeholder="Cole sua chave API aqui..."
                                        className="w-full p-2 border rounded focus:ring-2 focus:ring-orange-500 outline-none text-sm"
                                    />
                                    <p className="text-xs text-slate-400 mt-1">
                                        {apiKey ? "Uma chave já foi fornecida pelo sistema, mas você pode sobrescrever aqui." : "Insira sua chave para usar a ferramenta."}
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Form Cards */}
                    <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-orange-600 space-y-6">
                        <div className="flex items-center gap-2 mb-2">
                            <GraduationCap className="w-5 h-5 text-orange-600" />
                            <h3 className="text-lg font-bold text-slate-800">Dados da Avaliação</h3>
                            {isDetecting && (
                                <span className="ml-auto text-xs font-bold text-orange-600 animate-pulse flex items-center gap-1">
                                    <Loader className="w-3 h-3 animate-spin" /> Detectando dados...
                                </span>
                            )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Área</label>
                                <select
                                    value={area} onChange={(e) => setArea(e.target.value)}
                                    className="w-full p-2.5 border rounded-lg bg-slate-50 text-sm focus:ring-2 focus:ring-orange-500 outline-none"
                                >
                                    <option value="">Selecione...</option>
                                    <option value="Ciências da Natureza">Ciências da Natureza</option>
                                    <option value="Ciências Humanas">Ciências Humanas</option>
                                    <option value="Linguagens">Linguagens</option>
                                    <option value="Matemática">Matemática</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Disciplina</label>
                                <select
                                    value={disciplina} onChange={(e) => setDisciplina(e.target.value)}
                                    className="w-full p-2.5 border rounded-lg bg-slate-50 text-sm focus:ring-2 focus:ring-orange-500 outline-none"
                                >
                                    <option value="">Selecione...</option>
                                    {disciplinasList.map(d => <option key={d} value={d}>{d}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Série</label>
                                <select
                                    value={serie} onChange={(e) => setSerie(e.target.value)}
                                    className="w-full p-2.5 border rounded-lg bg-slate-50 text-sm focus:ring-2 focus:ring-orange-500 outline-none"
                                >
                                    <option value="">Selecione...</option>
                                    <option value="4º Ano EF">4º Ano EF</option>
                                    <option value="5º Ano EF">5º Ano EF</option>
                                    <option value="6º Ano EF">6º Ano EF</option>
                                    <option value="7º Ano EF">7º Ano EF</option>
                                    <option value="8º Ano EF">8º Ano EF</option>
                                    <option value="9º Ano EF">9º Ano EF</option>
                                    <option value="1ª Série EM">1ª Série EM</option>
                                    <option value="2ª Série EM">2ª Série EM</option>
                                    <option value="3ª Série EM">3ª Série EM</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Trimestre</label>
                                <select
                                    value={trimestre} onChange={(e) => setTrimestre(e.target.value)}
                                    className="w-full p-2.5 border rounded-lg bg-slate-50 text-sm focus:ring-2 focus:ring-orange-500 outline-none"
                                >
                                    <option value="">Selecione...</option>
                                    <option value="1º Trimestre">1º Trimestre</option>
                                    <option value="2º Trimestre">2º Trimestre</option>
                                    <option value="3º Trimestre">3º Trimestre</option>
                                </select>
                            </div>
                        </div>

                        <div className="border-t border-slate-100 pt-4 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">Anexar Arquivos (PDF ou Imagem)</label>
                                <div
                                    className="border-2 border-dashed border-slate-300 rounded-lg p-6 hover:bg-slate-50 transition-colors cursor-pointer text-center group"
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        accept=".pdf,image/*"
                                        className="hidden"
                                        multiple
                                        onChange={handleFileSelect}
                                    />
                                    <div className="flex flex-col items-center gap-2 text-slate-500 group-hover:text-orange-600 transition-colors">
                                        <Upload className="w-8 h-8 text-orange-300 group-hover:text-orange-500" />
                                        <span className="text-sm font-medium">Clique para adicionar PDF ou Imagens</span>
                                    </div>
                                </div>

                                {attachments.length > 0 && (
                                    <div className="mt-3 space-y-2">
                                        {attachments.map((att, idx) => (
                                            <div key={idx} className="flex items-center justify-between bg-orange-50 p-2 rounded border border-orange-100 text-sm">
                                                <div className="flex items-center gap-2 truncate">
                                                    {att.mimeType.includes('pdf') ? <FileIcon size={16} className="text-orange-500" /> : <ImageIcon size={16} className="text-orange-500" />}
                                                    <span className="truncate max-w-[200px] font-medium text-orange-900">{att.name}</span>
                                                </div>
                                                <button onClick={() => removeAttachment(idx)} className="text-slate-400 hover:text-red-500 p-1">
                                                    <X size={16} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">Ou Cole o Texto das Questões</label>
                                <textarea
                                    value={questoes}
                                    onChange={(e) => setQuestoes(e.target.value)}
                                    className="w-full h-40 p-3 border rounded-lg bg-slate-50 font-mono text-sm focus:ring-2 focus:ring-orange-500 outline-none resize-y"
                                    placeholder="Cole aqui o texto..."
                                ></textarea>
                            </div>
                        </div>

                        {error && (
                            <div className="p-4 bg-red-50 text-red-700 rounded-lg flex items-start gap-3 border border-red-100">
                                <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
                                <div className="text-sm">{error}</div>
                            </div>
                        )}

                        <button
                            onClick={generateValidation}
                            disabled={isLoading}
                            className={`
                                w-full py-4 rounded-xl font-bold text-white shadow-lg transition-all flex justify-center items-center gap-2
                                ${isLoading ? 'bg-slate-400 cursor-not-allowed' : 'bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 hover:-translate-y-1'}
                            `}
                        >
                            {isLoading ? (
                                <>
                                    <Loader className="w-5 h-5 animate-spin" /> Analisando Critérios Pedagógicos...
                                </>
                            ) : (
                                <>
                                    <BookOpen className="w-5 h-5" /> Validar Prova Agora
                                </>
                            )}
                        </button>
                    </div>
                </div>

                {/* Result Section */}
                {(validationReport || isLoading) && (
                    <div className="flex-1 lg:min-w-[60%] animate-in slide-in-from-right-4 duration-500 print-full-width">
                        <div className="bg-white rounded-lg shadow-xl overflow-hidden border border-slate-200 sticky top-4 print-no-overflow print-no-sticky">

                            {/* Toolbar */}
                            <div className="bg-slate-100 p-3 border-b flex justify-between items-center print:hidden">
                                <span className="text-sm font-semibold text-slate-600 flex items-center gap-2">
                                    <FileText className="w-4 h-4" /> Relatório de Validação
                                </span>
                                <div className="flex gap-2">
                                    <button onClick={copyToClipboard} className="p-2 hover:bg-white rounded text-slate-600 transition-colors" title="Copiar Texto">
                                        <Copy className="w-4 h-4" />
                                    </button>
                                    <button onClick={handlePrint} className="flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white text-sm px-3 py-1.5 rounded transition-colors">
                                        <Printer className="w-4 h-4" /> Imprimir / PDF
                                    </button>
                                </div>
                            </div>

                            {/* Content */}
                            <div className="p-8 print:p-0" id="report-content">
                                {/* Print Header */}
                                <div className="hidden print:block mb-8 border-b-2 border-black pb-4">
                                    <h1 className="text-2xl font-bold">Relatório de Validação de Prova</h1>
                                    <div className="grid grid-cols-2 mt-2 text-sm">
                                        <p><strong>Disciplina:</strong> {disciplina}</p>
                                        <p><strong>Série:</strong> {serie}</p>
                                        <p><strong>Área:</strong> {area}</p>
                                        <p><strong>Data:</strong> {new Date().toLocaleDateString()}</p>
                                    </div>
                                </div>

                                {/* AI Output */}
                                {validationStep === "" && validationReport ? (
                                    <div
                                        className="prose prose-orange max-w-none text-slate-800 leading-relaxed"
                                        dangerouslySetInnerHTML={{ __html: validationReport }}
                                    />
                                ) : questoesList.length > 0 ? (
                                    <div className="space-y-6">
                                        <div className="border-b pb-4 mb-4">
                                            <h2 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                                                Status do Processamento:
                                                {validationStep === 'extraindo' && <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded animate-pulse">Extraindo questões...</span>}
                                                {validationStep === 'validando' && <span className="text-xs bg-orange-100 text-orange-800 px-2 py-0.5 rounded animate-pulse">Validando questões...</span>}
                                                {validationStep === 'concluindo' && <span className="text-xs bg-purple-100 text-purple-800 px-2 py-0.5 rounded animate-pulse">Consolidando análise global...</span>}
                                            </h2>
                                            {validationStep === 'validando' && (
                                                <div className="w-full bg-slate-100 h-2 rounded-full mt-3 overflow-hidden">
                                                    <div 
                                                        className="bg-orange-500 h-full transition-all duration-300"
                                                        style={{ 
                                                            width: `${(Object.keys(analisesIndividual).length / questoesList.length) * 100}%` 
                                                        }}
                                                    />
                                                </div>
                                            )}
                                        </div>

                                        <div className="space-y-4">
                                            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Parte 1: Análise Questão a Questão</h3>
                                            {questoesList.map(q => {
                                                const status = statusValida[q.numero];
                                                const reportHtml = analisesIndividual[q.numero];

                                                return (
                                                    <div key={q.numero} className="transition-all duration-300">
                                                        {status === 'concluido' && reportHtml ? (
                                                            <div dangerouslySetInnerHTML={{ __html: reportHtml }} />
                                                        ) : status === 'validando' ? (
                                                            <div className="p-4 bg-orange-50/50 border border-orange-200 border-l-4 border-l-orange-400 rounded-r-xl flex items-center gap-3">
                                                                <Loader className="w-4 h-4 text-orange-500 animate-spin shrink-0" />
                                                                <span className="text-xs font-medium text-orange-800 animate-pulse">Validando Questão {q.numero}...</span>
                                                            </div>
                                                        ) : status === 'erro' && reportHtml ? (
                                                            <div dangerouslySetInnerHTML={{ __html: reportHtml }} />
                                                        ) : (
                                                            <div className="p-4 bg-slate-50 border border-slate-200 border-l-4 border-l-slate-300 rounded-r-xl flex items-center gap-3 opacity-60">
                                                                <span className="w-1.5 h-1.5 rounded-full bg-slate-400 shrink-0"></span>
                                                                <span className="text-xs font-medium text-slate-500">Questão {q.numero} na fila de validação...</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        {analiseGlobal ? (
                                            <div dangerouslySetInnerHTML={{ __html: analiseGlobal }} />
                                        ) : validationStep === 'concluindo' ? (
                                            <div className="p-6 bg-purple-50 border border-purple-200 border-l-4 border-l-purple-500 rounded-r-xl flex items-center gap-3">
                                                <Loader className="w-5 h-5 text-purple-500 animate-spin shrink-0" />
                                                <span className="text-sm font-medium text-purple-900 animate-pulse">Gerando Nota Geral, Veredito e Plano de Ação...</span>
                                            </div>
                                        ) : null}
                                    </div>
                                ) : (
                                    <div className="py-20 flex flex-col items-center text-center text-slate-400">
                                        <div className="bg-slate-100 p-4 rounded-full mb-4 animate-pulse">
                                            {validationStep === 'extraindo' ? (
                                                <Loader className="w-8 h-8 text-orange-500 animate-spin" />
                                            ) : (
                                                <Loader className="w-8 h-8 text-slate-300 animate-spin" />
                                            )}
                                        </div>
                                        <p className="text-sm font-medium">
                                            {validationStep === 'extraindo' 
                                                ? 'Analisando documento e separando as questões de forma estruturada...' 
                                                : 'A IA está lendo sua prova e validando os critérios...'}
                                        </p>
                                    </div>
                                )}

                                {/* Print Footer */}
                                <div className="hidden print:block mt-12 pt-4 border-t border-slate-300 text-center text-xs text-slate-500">
                                    <p>Documento gerado via Plataforma de Validação Pedagógica com suporte de IA.</p>
                                    <p>A validação final é de responsabilidade do Coordenador Pedagógico.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
}
